// React bridge for the browser broadcast engine (web-only).
import { useEffect, useRef, useState, useCallback } from "react";
import { createBroadcastEngine } from "@/components/streaming/engine/broadcastEngine";

export default function useBroadcastEngine({ relayUrl, streamKey }) {
  const engineRef = useRef(null);
  const [state, setState] = useState("idle");
  const [stats, setStats] = useState(null);
  const [levels, setLevels] = useState([0, 0]);
  const [muted, setMutedState] = useState(false);
  const [error, setError] = useState(null);
  const [recording, setRecording] = useState(null); // null | "armed" | "recording" | "saved"
  const [screenSharing, setScreenSharing] = useState(false);
  const [renewedStream, setRenewedStream] = useState(null); // engine re-acquired capture (sleep/unplug)

  // Recreate the engine when the publish key changes (e.g. switching the own
  // feed from {key} to {key}-CAM1 when multicam turns on).
  const keyRef = useRef(streamKey);
  if (engineRef.current && keyRef.current !== streamKey) {
    engineRef.current.destroy();
    engineRef.current = null;
    setState("idle");
    setStats(null);
    setRecording(null);
    setScreenSharing(false);
    setRenewedStream(null);
    setError(null);
  }
  if (!engineRef.current && streamKey) {
    keyRef.current = streamKey;
    engineRef.current = createBroadcastEngine({
      relayUrl,
      streamKey,
      onState: setState,
      onStats: setStats,
      onError: (code, message) => setError({ code, message }),
      onRecordingChange: setRecording,
      onScreenShareChange: setScreenSharing,
      onStreamRenewed: setRenewedStream,
    });
  }

  useEffect(() => {
    // read the ref at cleanup time — the engine may have been recreated
    return () => engineRef.current?.destroy();
  }, []);

  // VU meter polling — cheap AnalyserNode reads at ~12fps.
  useEffect(() => {
    if (!["preview", "connecting", "publishing", "reconnecting", "paused"].includes(state)) return;
    const id = setInterval(() => setLevels(engineRef.current?.getAudioLevels() ?? [0, 0]), 80);
    return () => clearInterval(id);
  }, [state]);

  const setMuted = useCallback((next) => {
    engineRef.current?.setMuted(next);
    setMutedState(next);
  }, []);

  return {
    engine: engineRef.current,
    supported: engineRef.current?.supported ?? false,
    state,
    stats,
    levels,
    muted,
    setMuted,
    recording,
    screenSharing,
    renewedStream,
    error,
    clearError: useCallback(() => setError(null), []),
  };
}
